import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  StackSeparator,
  Flex,
  Stack,
  Spacer,
  Spinner,
  Text,
  HStack,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import QuickSearchForm from '@/components/ui/quick-search-form';
import BrowseContent from './browse-content';
import useWorkspace from '@/lib/hooks/use-workspace';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { ExtendedWorkspace, ContentType } from '@/lib/types';
import api from '@/utils/api';

interface Type extends ContentType {
  content_counts: number;
  workspace_content_type_id: number;
  workspace_content_type_reference_id: string;
}

interface Workspace extends ExtendedWorkspace {
  public_workspace_id: number | null;
  checked: boolean;
  types: Type[];
}

interface Props {
  id: number;
  name: string;
  reference_id: string;
}

export default function PartnerContent() {
  const contentDrawerModal = useModalToggle();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<Workspace[]>([]);
  const [loadingItems, setLoadingItems] = useState<{ [key: number]: boolean }>({});
  const [selectedItem, setSelectedItem] = useState<Workspace>();
  const [selectedTypes, setSelectedTypes] = useState<Props[]>([]);
  const { workspace } = useWorkspace();

  const fetchData = useCallback(
    async (searchTerm = '') => {
      try {
        setIsLoading(true);
        const resWorkspace = await api.get(
          `workspace/public/${workspace.sport_type_id}?search_term=${searchTerm}`,
        );
        const resSelections = await api.get(`public-workspace-selections/${workspace.id}`);
        const selectedWorkspaces = resSelections.data.data;

        const workspaces = resWorkspace.data.data.map(
          (data: { workspace: Workspace; content_types: ContentType[] }) => {
            const selection = selectedWorkspaces.find(
              (sel: { public_workspace_id: number }) =>
                sel.public_workspace_id === data.workspace.id,
            );
            return {
              ...data.workspace,
              public_workspace_id: selection ? selection.id : null,
              checked: !!selection,
              types: [...data.content_types],
            };
          },
        );
        const filteredItems = workspaces.filter((item: Workspace) => item.id !== workspace.id);
        setItems(filteredItems.sort((a: Workspace, b: Workspace) => a.name.localeCompare(b.name)));
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [workspace],
  );

  const handleSearch = (search: string) => {
    fetchData(search);
  };

  useEffect(() => {
    if (workspace && workspace.sport_type_id) {
      fetchData();
    }
  }, [fetchData, workspace]);

  const AddWorkspace = async (item: Workspace, checked: boolean) => {
    setLoadingItems((prev) => ({ ...prev, [item.id]: true }));

    try {
      if (checked) {
        await api.post(`public-workspace-selections`, {
          workspace_id: workspace.id,
          public_workspace_id: item.id,
        });
        fetchData();
        toaster.create({
          type: 'success',
          title: 'Partner content added to workspace',
        });
        return;
      }

      await api.delete(`public-workspace-selections/${item.public_workspace_id}`);
      fetchData();
      toaster.create({
        type: 'success',
        title: 'Partner content removed from workspace',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to add content to workspace',
      });
    } finally {
      setLoadingItems((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  const handleBrowseContent = (item: Workspace) => {
    const types = item.types
      .filter((type) => type.content_counts > 0)
      .map((type) => ({
        id: type.id,
        name: type.name,
        reference_id: type.workspace_content_type_reference_id || type.reference_id,
      }));
    const filteredTypes = types.filter(
      (type) => type.reference_id !== undefined && type.reference_id !== null,
    );
    setSelectedTypes(filteredTypes);
    setSelectedItem(item);
    contentDrawerModal.open();
  };

  return (
    <Card.Root>
      <Card.Header pl={{ base: 4, lg: 6 }}>
        <Card.Title>Partner content</Card.Title>
      </Card.Header>
      <Card.Body pl={{ base: 4, lg: 6 }} pb={10}>
        <HStack mb={8}>
          <QuickSearchForm handleSearch={handleSearch} />
        </HStack>
        {isLoading ? (
          <Stack flex='1'>
            <Skeleton variant='shine' height='12' />
            <Skeleton variant='shine' height='12' />
            <Skeleton variant='shine' height='12' />
          </Stack>
        ) : (
          <Stack gap={4} separator={<StackSeparator />} minHeight={100}>
            {items.map((item) => (
              <Box key={item.id}>
                <Flex w={'full'} alignItems='center' flexWrap='wrap'>
                  <Stack gap={1}>
                    <Text fontSize={'sm'} fontWeight={'medium'} _hover={{ color: 'brand' }}>
                      {item.name}
                    </Text>
                    <Text fontSize={'xs'} color='fg.muted'>
                      {item.description}
                    </Text>
                  </Stack>
                  <Spacer />
                  <Stack direction={{ base: 'column', lg: 'row' }} gap={{ base: 3, lg: 6 }}>
                    <Checkbox
                      colorPalette='brand'
                      size='sm'
                      checked={item.checked}
                      onCheckedChange={(e) => AddWorkspace(item, !!e.checked)}
                    >
                      <HStack>
                        <Text>Add to workspace</Text>
                        {loadingItems[item.id] && <Spinner size={'sm'} />}
                      </HStack>
                    </Checkbox>
                    <Button
                      colorPalette='brand'
                      size={'xs'}
                      variant='surface'
                      onClick={() => handleBrowseContent(item)}
                    >
                      Browse content
                    </Button>
                  </Stack>
                </Flex>
              </Box>
            ))}
          </Stack>
        )}
      </Card.Body>

      {selectedItem && (
        <BrowseContent
          {...contentDrawerModal.props}
          workspace={selectedItem}
          types={selectedTypes}
        />
      )}
    </Card.Root>
  );
}
